import React from 'react'

import { Control, Controller, useWatch } from 'react-hook-form'
import {
  Accordion,
  ActionButton,
  CustomCheckboxInput,
  QuestionCard,
  SimpleText,
  XStack,
  YStack, XPReactNative } from '@my/ui'

import { AccordionItem, DropdownField, InputField } from '../helpers/Helper'
import { confirmationOptions, incomeChange, incomeStability, April2026UpdatedOptions } from '../data'
import images from '../../../assets/images'
import { color, safeBigNum } from '@my/util'
const { StyleSheet } = XPReactNative

interface IncomeAndEmploymentProps {
  control: Control<any>
}

// Reusable Frequency Buttons Component
const FrequencyButtons: React.FC<{
  control: Control<any>
  name: string
}> = ({ control, name }) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { onChange, value } }) => (
      <XStack marginVertical={10}>
        {['Monthly', 'Quarterly', 'Annually'].map((item) => (
          <ActionButton
            key={item}
            unstyled
            width={88}
            height={34}
            alignItems="center"
            justifyContent="center"
            marginHorizontal={5}
            bg={value === item ? color.tzaGreen : color.dullGrey}
            onPress={() => onChange(item)}
          >
            <SimpleText color={value === item ? color.white : color.lightGrey}>{item}</SimpleText>
          </ActionButton>
        ))}
      </XStack>
    )}
  />
)

export const IncomeAndEmployment: React.FC<IncomeAndEmploymentProps> = ({ control }) => {
  const incomeChangeAnticipated = useWatch({name: "Income - Change"});
  const additionalMonthlyIncomeFormValue = useWatch({name: "Income - Monthly (misc)"});
  const monthlyMiscIsPositive = safeBigNum((''+additionalMonthlyIncomeFormValue).replace('$', '').trim()).isGreaterThan(0);

  return (
    <>
      {/* Annual Income Section */}
      <QuestionCard questionGroupTitle="What is your approximate annual gross income (before taxes and deductions)?">
        <DropdownField
          control={control}
          name="Income - Annual Income (gross)"
          items={April2026UpdatedOptions['Income'].annualGrossIncome} 
          native
        />
      </QuestionCard>

      {/* Monthly Income Section */}
      <QuestionCard questionGroupTitle="What is your total monthly income, including all sources such as salary, bonuses, and other compensations?">
        <InputField
          control={control}
          name="Income - Monthly Salary (net)"
          placeholder="Monthly Take Home Pay (after taxes & benefits contributions)"
          icon={images.money}
        />
        <InputField
          control={control}
          name="Income - Monthly (misc)"
          placeholder="Additional Monthly Income (after taxes, e.g. money from family, side hustles, etc.)"
          icon={images.money}
        />
        {monthlyMiscIsPositive && <DropdownField
          control={control}
          name="Income - Additional Monthly Income Source"
          label="Is your additional income from a W-2 job, self-employment/1099, rental property, or investments?"
          showLabel
          items={April2026UpdatedOptions['Income'].additionalIncomeSource} 
          native
        />}
      </QuestionCard>

      <QuestionCard questionGroupTitle="Is your income variable (does it change significantly month to month)?">
        <YStack marginVertical={10}>
          <CustomCheckboxInput
            options={confirmationOptions}
            control={control}
            name="Income - Is Variable month-to-month"
          />
        </YStack>
      </QuestionCard>

      {/* Income Change Section */}
      <QuestionCard questionGroupTitle="Do you anticipate any changes that might impact your income in the near future (e.g., promotions, changing jobs)?">
        <YStack marginVertical={10}>
          <CustomCheckboxInput
            options={confirmationOptions}
            control={control}
            name="Income - Change"
          />
        </YStack>
      </QuestionCard>

      {/* Anticipated Income Change Section */}
      {incomeChangeAnticipated && <QuestionCard questionGroupTitle="What type of income impacting change do you anticipate?">
        <YStack marginVertical={10}>
          <CustomCheckboxInput
            options={incomeChange}
            control={control}
            name="Income - Change Anticipate"
            circle
          />
        </YStack>
      </QuestionCard>}

      {/* Description of Anticipated Change */}
      {incomeChangeAnticipated && <QuestionCard questionGroupTitle="Please provide a description of the anticipated change.">
        <InputField
          control={control}
          name="Income - Anticipate Description"
          placeholder="Misc. Additional Annual Income (e.g., lump sum bonus, additional work)"
          icon={images.money}
        />
      </QuestionCard>}

      {/* Employment Stability Section */}
      <QuestionCard questionGroupTitle="How stable is your current employment situation?">
        <YStack marginVertical={10}>
          <CustomCheckboxInput
            options={incomeStability}
            control={control}
            name="Income - Employment Stability"
          />
        </YStack>
      </QuestionCard>
    </>
  )
}

const styles = StyleSheet.create({
  cancelButton: { color: '#848484', borderColor: '#848484', fontSize: 17 },
})
